import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Calendar, CheckCircle, Clock, AlertCircle, XCircle, RotateCcw, Baby, Zap, Syringe, List } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import AnimalSearchSelect from '../components/AnimalSearchSelect';

const STATUS_INFO = {
  pendente:  { label:'Pendente',  badge:'badge-yellow', icon:Clock },
  concluido: { label:'Concluído', badge:'badge-green',  icon:CheckCircle },
  atrasado:  { label:'Atrasado',  badge:'badge-red',    icon:AlertCircle },
  cancelado: { label:'Cancelado', badge:'badge-gray',   icon:XCircle },
};

const TIPO_CORES = {
  vacinacao:'#2563EB', pesagem:'#16A34A', parto_previsto:'#EA580C',
  cobertura:'#7C3AED', visita_veterinaria:'#0891B2', desmame:'#D97706',
  intervencao:'#DC2626', administrativo:'#0D9488', outro:'#6B7280'
};

const TIPOS_EVENTO = ['vacinacao','pesagem','parto_previsto','cobertura','visita_veterinaria','desmame','administrativo','outro'];
const TIPO_LABELS_AGENDA = { vacinacao:'Vacinação', pesagem:'Pesagem', parto_previsto:'Parto previsto', cobertura:'Cobertura', visita_veterinaria:'Visita veterinária', desmame:'Desmame', administrativo:'Administrativo (propriedade)', outro:'Outro' };

const FONTE_ICON = { parto_previsto: Baby, desmame: Clock, intervencao: Syringe };
const FONTE_COR  = { parto_previsto:'#EA580C', desmame:'#D97706', intervencao:'#DC2626', manual:'#6B7280' };

export default function Agenda() {
  const [aba, setAba] = useState('consolidada');

  // --- ABA CONSOLIDADA ---
  const [consolidada, setConsolidada] = useState([]);
  const [loadingCons, setLoadingCons] = useState(true);

  const carregarConsolidada = useCallback(async () => {
    setLoadingCons(true);
    try { const { data } = await api.get('/agenda/consolidada'); setConsolidada(data); }
    catch { toast.error('Erro ao carregar agenda consolidada'); }
    finally { setLoadingCons(false); }
  }, []);

  // --- ABA MANUAL ---
  const [eventos, setEventos] = useState([]);
  const [animais, setAnimais] = useState([]);
  const [lotes, setLotes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [editando, setEditando]     = useState(null);
  const [filtro, setFiltro]         = useState({ status:'', tipo:'' });
  const [form, setForm] = useState({ titulo:'', descricao:'', tipo:'outro', data_inicio:'', data_fim:'', animal_id:'', lote_id:'', alerta_email:true, alerta_telegram:false, alerta_antecedencia_horas:24 });
  const [relacionadoAnimal, setRelacionadoAnimal] = useState(true);

  const loadManual = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/agenda', { params: filtro }); setEventos(data); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => {
    carregarConsolidada();
    api.get('/animais', { params:{ situacao:'ativo', limit:500 } }).then(r => setAnimais(r.data.data || r.data));
    api.get('/lotes').then(r => setLotes(r.data));
  }, [carregarConsolidada]);

  useEffect(() => { loadManual(); }, [loadManual]);

  const abrir = (ev = null) => {
    setEditando(ev);
    setForm(ev
      ? { titulo:ev.titulo, descricao:ev.descricao||'', tipo:ev.tipo, data_inicio:ev.data_inicio?.replace('T',' ').substring(0,16)||'', data_fim:ev.data_fim?.replace('T',' ').substring(0,16)||'', animal_id:ev.animal_id||'', lote_id:ev.lote_id||'', alerta_email:ev.alerta_email, alerta_telegram:ev.alerta_telegram, alerta_antecedencia_horas:ev.alerta_antecedencia_horas }
      : { titulo:'', descricao:'', tipo:'outro', data_inicio:'', data_fim:'', animal_id:'', lote_id:'', alerta_email:true, alerta_telegram:false, alerta_antecedencia_horas:24 });
    setRelacionadoAnimal(ev ? !!ev.animal_id : true);
    setShowModal(true);
  };

  const alternarRelacionadoAnimal = (sim) => {
    setRelacionadoAnimal(sim);
    if (!sim) setForm(p => ({ ...p, animal_id:'' }));
  };

  const salvar = async () => {
    try {
      const payload = relacionadoAnimal ? form : { ...form, animal_id:'' };
      if (editando) await api.put(`/agenda/${editando.id}`, payload);
      else await api.post('/agenda', payload);
      toast.success(editando ? 'Evento atualizado!' : 'Evento criado!');
      setShowModal(false); loadManual();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const concluir = async (id) => {
    try { await api.put(`/agenda/${id}/concluir`); toast.success('Concluído!'); loadManual(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const reabrir = async (id) => {
    try { await api.put(`/agenda/${id}/reabrir`); toast.success('Reaberto!'); loadManual(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const cancelar = async (id) => {
    if (!window.confirm('Cancelar este evento?')) return;
    try { await api.put(`/agenda/${id}`, { ...eventos.find(e => e.id===id), status:'cancelado' }); toast.success('Cancelado'); loadManual(); }
    catch (e) { toast.error('Erro ao cancelar'); }
  };

  const remover = async (id) => {
    if (!window.confirm('Remover evento permanentemente?')) return;
    try { await api.delete(`/agenda/${id}`); toast.success('Removido'); loadManual(); }
    catch { toast.error('Erro'); }
  };

  const atrasados = eventos.filter(e => e.status === 'atrasado');
  const proximos7 = eventos.filter(e => {
    const d = new Date(e.data_inicio); const now = new Date();
    return d >= now && d <= new Date(now.getTime() + 7*86400000) && e.status === 'pendente';
  });

  // Consolidada: separar por seção
  const consHoje   = consolidada.filter(e => { const d = new Date(e.data); const t = new Date(); return d.toDateString() === t.toDateString(); });
  const consSemana = consolidada.filter(e => { const d = new Date(e.data); const now = new Date(); const fim = new Date(now.getTime()+7*86400000); return d > new Date() && d <= fim; });
  const consResto  = consolidada.filter(e => { const d = new Date(e.data); const fim = new Date(new Date().getTime()+7*86400000); return d > fim; });
  const consAtras  = consolidada.filter(e => new Date(e.data) < new Date() && e.fonte !== 'manual');

  const renderConsolidadaItem = (e, i) => {
    const Icon = FONTE_ICON[e.tipo] || Calendar;
    const cor  = FONTE_COR[e.tipo] || FONTE_COR.manual;
    const data = e.data ? new Date(e.data) : null;
    const diasFalta = data ? Math.ceil((data - new Date()) / (1000*60*60*24)) : null;
    return (
      <div key={`${e.tipo}_${e.id||i}`} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 16px', borderBottom:'1px solid #F3F4F6', background: diasFalta !== null && diasFalta < 0 ? '#FFF5F5' : undefined }}>
        <div style={{ width:36, height:36, borderRadius:8, background:`${cor}18`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Icon size={16} color={cor}/>
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:600, fontSize:14 }}>{e.titulo}</div>
          <div style={{ fontSize:12, color:'var(--text-secondary)', display:'flex', gap:12, marginTop:2 }}>
            {data && <span>📅 {data.toLocaleDateString('pt-BR')}</span>}
            {e.brinco && <span>🐑 {e.brinco}{e.nome ? ` — ${e.nome}` : ''}</span>}
            {e.lote_nome && <span>📦 {e.lote_nome}</span>}
            {e.fonte === 'auto' && <span style={{ color:'#6366F1', fontSize:11 }}>⚡ automático</span>}
          </div>
        </div>
        <div>
          {diasFalta !== null && diasFalta < 0
            ? <span className="badge badge-red">Atrasado {Math.abs(diasFalta)}d</span>
            : diasFalta !== null && diasFalta <= 7
              ? <span className="badge badge-yellow">Em {diasFalta}d</span>
              : diasFalta !== null
                ? <span className="badge badge-gray">{diasFalta}d</span>
                : null}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1>Agenda</h1>
          <p>{consolidada.length} eventos consolidados · {atrasados.length} atrasados</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setAba('manual'); abrir(); }}><Plus size={16}/> Novo Evento</button>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:2, marginBottom:16, borderBottom:'2px solid #E5E7EB' }}>
        {[['consolidada','Visão Consolidada',Zap],['manual','Agenda Manual',List]].map(([key,label,Icon]) => (
          <button key={key} onClick={() => setAba(key)}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 18px', border:'none', borderBottom: aba===key ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom:-2,
              background:'none', cursor:'pointer', fontWeight: aba===key ? 600 : 400, color: aba===key ? 'var(--color-primary)' : 'var(--text-secondary)', fontSize:14 }}>
            <Icon size={15}/> {label}
          </button>
        ))}
      </div>

      {/* ABA CONSOLIDADA */}
      {aba === 'consolidada' && (
        <div>
          {loadingCons ? (
            <div style={{ textAlign:'center', padding:60, color:'var(--text-secondary)' }}>Carregando agenda...</div>
          ) : consolidada.length === 0 ? (
            <div className="empty-state"><p>Nenhum evento encontrado na agenda consolidada.</p></div>
          ) : (
            <>
              {consAtras.length > 0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontWeight:600, color:'#DC2626', fontSize:13, marginBottom:6, display:'flex', alignItems:'center', gap:6 }}><AlertCircle size={14}/> ATRASADOS ({consAtras.length})</div>
                  <div className="card" style={{ padding:0, overflow:'hidden' }}>{consAtras.map(renderConsolidadaItem)}</div>
                </div>
              )}
              {consHoje.length > 0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontWeight:600, color:'#D97706', fontSize:13, marginBottom:6 }}>HOJE ({consHoje.length})</div>
                  <div className="card" style={{ padding:0, overflow:'hidden' }}>{consHoje.map(renderConsolidadaItem)}</div>
                </div>
              )}
              {consSemana.length > 0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontWeight:600, color:'var(--color-primary)', fontSize:13, marginBottom:6 }}>PRÓXIMOS 7 DIAS ({consSemana.length})</div>
                  <div className="card" style={{ padding:0, overflow:'hidden' }}>{consSemana.map(renderConsolidadaItem)}</div>
                </div>
              )}
              {consResto.length > 0 && (
                <div>
                  <div style={{ fontWeight:600, color:'var(--text-secondary)', fontSize:13, marginBottom:6 }}>MAIS ADIANTE ({consResto.length})</div>
                  <div className="card" style={{ padding:0, overflow:'hidden' }}>{consResto.map(renderConsolidadaItem)}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ABA MANUAL */}
      {aba === 'manual' && (
        <div>
          {atrasados.length > 0 && (
            <div style={{ background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', marginBottom:16 }}>
              <div style={{ fontWeight:600, color:'#DC2626', marginBottom:6, display:'flex', alignItems:'center', gap:6 }}><AlertCircle size={16}/> {atrasados.length} evento(s) atrasado(s)</div>
              {atrasados.slice(0,3).map(e => <div key={e.id} style={{ fontSize:13, color:'#7F1D1D' }}>• {e.titulo} — {new Date(e.data_inicio).toLocaleDateString('pt-BR')}</div>)}
            </div>
          )}

          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ display:'flex', gap:12 }}>
              <select value={filtro.status} onChange={e => setFiltro(p => ({ ...p, status:e.target.value }))} style={{ width:'auto' }}>
                <option value="">Todos os status</option>
                {['pendente','concluido','atrasado','cancelado'].map(s => <option key={s}>{s}</option>)}
              </select>
              <select value={filtro.tipo} onChange={e => setFiltro(p => ({ ...p, tipo:e.target.value }))} style={{ width:'auto' }}>
                <option value="">Todos os tipos</option>
                {TIPOS_EVENTO.map(t => <option key={t} value={t}>{TIPO_LABELS_AGENDA[t]}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {loading ? <div className="card" style={{ textAlign:'center', padding:40 }}>Carregando...</div>
            : eventos.length === 0 ? <div className="card"><div className="empty-state"><p>Nenhum evento na agenda</p></div></div>
            : eventos.map(ev => {
              const info = STATUS_INFO[ev.status] || STATUS_INFO.pendente;
              const Icon = info.icon;
              const isAtrasado = ev.status === 'atrasado';
              const isPendente = ev.status === 'pendente';
              const isConcluido = ev.status === 'concluido';
              const isCancelado = ev.status === 'cancelado';
              return (
                <div key={ev.id} className="card" style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 20px', borderLeft:`4px solid ${TIPO_CORES[ev.tipo]||'#666'}`, background: isAtrasado?'#FFF5F5':isConcluido?'#F0FDF4':isCancelado?'#F9FAFB':'var(--bg-card)', opacity:isCancelado?0.7:1 }}>
                  <div style={{ width:40, height:40, borderRadius:10, background:(TIPO_CORES[ev.tipo]||'#666')+'20', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Calendar size={18} color={TIPO_CORES[ev.tipo]||'#666'}/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:15, textDecoration:isCancelado?'line-through':'none' }}>{ev.titulo}</div>
                    <div style={{ fontSize:13, color:'var(--text-secondary)', display:'flex', gap:16, marginTop:2 }}>
                      <span>📅 {new Date(ev.data_inicio).toLocaleString('pt-BR',{ day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit' })}</span>
                      {ev.animal_brinco && <span>🐑 {ev.animal_brinco}</span>}
                      {ev.lote_nome && <span>📦 {ev.lote_nome}</span>}
                    </div>
                    {ev.descricao && <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:2 }}>{ev.descricao}</div>}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                    <span className={`badge ${info.badge}`} style={{ display:'flex', alignItems:'center', gap:4 }}><Icon size={11}/> {info.label}</span>
                    {(isPendente||isAtrasado) && <button className="btn btn-secondary btn-sm" onClick={() => concluir(ev.id)} style={{ color:'#16A34A', display:'flex', alignItems:'center', gap:3, fontSize:12 }}><CheckCircle size={13}/> Concluir</button>}
                    {(isPendente||isAtrasado) && <button className="btn btn-secondary btn-sm" onClick={() => cancelar(ev.id)} style={{ color:'#6B7280', fontSize:12 }} title="Cancelar"><XCircle size={13}/></button>}
                    {(isConcluido||isCancelado) && <button className="btn btn-secondary btn-sm" onClick={() => reabrir(ev.id)} style={{ display:'flex', alignItems:'center', gap:3, fontSize:12 }}><RotateCcw size={13}/> Reabrir</button>}
                    <button className="btn btn-secondary btn-sm" onClick={() => abrir(ev)}>✎</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => remover(ev.id)} style={{ color:'#DC2626' }}>🗑</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header"><h2>{editando ? 'Editar Evento' : 'Novo Evento'}</h2><button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Título *</label><input value={form.titulo} onChange={e => setForm(p => ({ ...p, titulo:e.target.value }))} placeholder="Ex: Vacinação clostridioses — Lote A"/></div>
              <div className="form-row">
                <div className="form-group"><label>Tipo</label>
                  <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo:e.target.value }))}>
                    {TIPOS_EVENTO.map(t => <option key={t} value={t}>{TIPO_LABELS_AGENDA[t]}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Alerta (horas antes)</label>
                  <select value={form.alerta_antecedencia_horas} onChange={e => setForm(p => ({ ...p, alerta_antecedencia_horas:parseInt(e.target.value) }))}>
                    {[1,2,6,12,24,48,72].map(h => <option key={h} value={h}>{h}h antes</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Início *</label><input type="datetime-local" value={form.data_inicio} onChange={e => setForm(p => ({ ...p, data_inicio:e.target.value }))}/></div>
                <div className="form-group"><label>Fim</label><input type="datetime-local" value={form.data_fim} onChange={e => setForm(p => ({ ...p, data_fim:e.target.value }))}/></div>
              </div>
              <div className="form-group">
                <label>Relacionado a animal?</label>
                <div style={{ display:'flex', gap:16 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:14, cursor:'pointer' }}>
                    <input type="radio" name="relacionadoAnimal" checked={relacionadoAnimal} onChange={() => alternarRelacionadoAnimal(true)} style={{ width:'auto' }}/> Sim
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:14, cursor:'pointer' }}>
                    <input type="radio" name="relacionadoAnimal" checked={!relacionadoAnimal} onChange={() => alternarRelacionadoAnimal(false)} style={{ width:'auto' }}/> Não — atividade geral da propriedade
                  </label>
                </div>
              </div>
              <div className="form-row">
                {relacionadoAnimal && (
                  <div className="form-group"><label>Animal</label>
                    <AnimalSearchSelect value={form.animal_id} onChange={id => setForm(p => ({ ...p, animal_id:id }))} emptyLabel="Nenhum" />
                  </div>
                )}
                <div className="form-group"><label>Lote</label>
                  <select value={form.lote_id} onChange={e => setForm(p => ({ ...p, lote_id:e.target.value }))}>
                    <option value="">Nenhum</option>
                    {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group"><label>Descrição</label><textarea rows={2} value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao:e.target.value }))}/></div>
              <div style={{ display:'flex', gap:20 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:14, cursor:'pointer' }}>
                  <input type="checkbox" checked={form.alerta_email} onChange={e => setForm(p => ({ ...p, alerta_email:e.target.checked }))} style={{ width:'auto' }}/> Alerta por e-mail
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:14, cursor:'pointer' }}>
                  <input type="checkbox" checked={form.alerta_telegram} onChange={e => setForm(p => ({ ...p, alerta_telegram:e.target.checked }))} style={{ width:'auto' }}/> Alerta por Telegram
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={!form.titulo || !form.data_inicio}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
